/**
 * Time discipline.
 *
 * The platform stores timestamps in UTC and displays them in Pacific Time.
 * Batch periods are defined as "12:01 AM PT on day 1 of month → 12:00 AM PT
 * on day 1 of the following month" (per `REQUIREMENTS.md` NFR Time Discipline
 * and `API_CONTRACT.md` §4).
 *
 * DST transitions are explicit: this module never silently shifts an hour.
 * Backend timestamps always carry an offset; we never interpret a naive
 * timestamp as a particular zone.
 */
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

export const PACIFIC = "America/Los_Angeles";

export type BatchPeriod = {
  /** Inclusive start in UTC. Maps to `2026-03-01T00:01:00-07:00` for March 2026. */
  startUtc: Date;
  /** Exclusive end in UTC. Maps to `2026-04-01T00:00:00-07:00` for March 2026. */
  endUtc: Date;
  /** Human label, e.g. "March 2026". */
  label: string;
};

/**
 * Construct a monthly batch period in Pacific Time.
 *
 * NOTE: this returns the *instants* in UTC that correspond to the PT-local
 * boundaries. Daylight Saving boundaries are respected — e.g., a March
 * spring-forward period is 1 hour shorter in real time than a non-DST month.
 */
export function ptMonthlyBatchPeriod(year: number, month: number): BatchPeriod {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new RangeError(`month must be an integer 1-12; got ${month}`);
  }
  if (!Number.isInteger(year) || year < 2000 || year > 2100) {
    throw new RangeError(`year must be an integer 2000-2100; got ${year}`);
  }

  const startUtc = ptWallTimeToUtc(year, month, 1, 0, 1, 0); // 12:01 AM PT day 1
  const [nextYear, nextMonth] = month === 12 ? [year + 1, 1] : [year, month + 1];
  // 12:00 AM PT day 1 of NEXT month (exclusive end of current month's batch).
  // Wall-time 00:00 happens twice on fall-back; we use the second occurrence
  // (post-transition local midnight) which is the canonical batch boundary.
  const endUtc = ptWallTimeToUtc(nextYear, nextMonth, 1, 0, 0, 0);

  return {
    startUtc,
    endUtc,
    label: `${MONTH_NAMES[month - 1]} ${year}`,
  };
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Convert a Pacific wall-clock time to its UTC instant.
 * Uses Intl.DateTimeFormat to handle DST transitions deterministically.
 */
export function ptWallTimeToUtc(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  second: number,
): Date {
  // Resolve the offset *at the target wall time* via date-fns-tz. A naive
  // single-pass approach (assume wall == UTC, read the offset there, subtract)
  // picks the wrong offset for wall times within ~1h after a DST transition,
  // because the tentative instant can land on the other side of the boundary.
  // `fromZonedTime` resolves the zone offset at the wall time itself.
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  const naive = `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
  return fromZonedTime(naive, PACIFIC);
}

/**
 * Returns the offset (in ms) of `America/Los_Angeles` from UTC for the given
 * UTC instant. Negative during PST (UTC-08:00), e.g. -8 * 3600 * 1000.
 */
export function ptOffsetMs(instantUtc: Date): number {
  const offsetString = formatInTimeZone(instantUtc, PACIFIC, "xxx"); // "-08:00" / "-07:00"
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(offsetString);
  if (!match) {
    throw new Error(`Unexpected PT offset format: ${offsetString}`);
  }
  // Non-null assertions: the regex guarantees these groups exist when the match succeeds.
  // biome-ignore lint/style/noNonNullAssertion: groups guaranteed by successful regex match
  const sign = match[1]! === "-" ? -1 : 1;
  // biome-ignore lint/style/noNonNullAssertion: groups guaranteed by successful regex match
  const hours = Number.parseInt(match[2]!, 10);
  // biome-ignore lint/style/noNonNullAssertion: groups guaranteed by successful regex match
  const minutes = Number.parseInt(match[3]!, 10);
  return sign * (hours * 3600 + minutes * 60) * 1000;
}

/**
 * Whether the given UTC instant falls inside Pacific Daylight Time (UTC-07:00)
 * vs Pacific Standard Time (UTC-08:00).
 */
export function isPacificDaylightTime(instantUtc: Date): boolean {
  return ptOffsetMs(instantUtc) === -7 * 3600 * 1000;
}

/**
 * Render a UTC instant in Pacific Time with an explicit offset. ISO 8601.
 *   2026-03-31T00:01:00-07:00
 */
export function formatPacific(instantUtc: Date): string {
  return formatInTimeZone(instantUtc, PACIFIC, "yyyy-MM-dd'T'HH:mm:ssxxx");
}

/**
 * Render a UTC instant in UTC with `Z` suffix. ISO 8601.
 *   2026-03-31T07:01:00Z
 */
export function formatUtc(instantUtc: Date): string {
  return formatInTimeZone(instantUtc, "UTC", "yyyy-MM-dd'T'HH:mm:ss'Z'");
}

/**
 * Both representations of the same instant, suitable for tooltips:
 *   { pt: "2026-03-31T00:01:00-07:00", utc: "2026-03-31T07:01:00Z" }
 */
export function formatBoth(instantUtc: Date): { pt: string; utc: string } {
  return { pt: formatPacific(instantUtc), utc: formatUtc(instantUtc) };
}

/**
 * Parse an ISO 8601 string WITH an explicit offset (`Z` or `±HH:MM`) into a
 * UTC Date. Throws on naive timestamps — we never guess the zone.
 */
export function parseInstantWithOffset(iso: string): Date {
  if (typeof iso !== "string") {
    throw new TypeError(`parseInstantWithOffset expected string, got ${typeof iso}`);
  }
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) {
    throw new RangeError(
      `parseInstantWithOffset: ISO 8601 string must carry an explicit offset; got "${iso}"`,
    );
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError(`parseInstantWithOffset: invalid ISO 8601 string: "${iso}"`);
  }
  return d;
}

/** Convert a UTC instant to a `Date` in the Pacific zone (for `date-fns` ops). */
export function asPacificDate(instantUtc: Date): Date {
  return toZonedTime(instantUtc, PACIFIC);
}
